import { orgSlugSchema } from '@mapshare/schema';

/**
 * Slugs that may never be handed to an organization.
 *
 * An organization slug becomes a subdomain (`mycompany.mapshare.fyi`), so a
 * slug that collides with infrastructure would shadow it. The list is kept in
 * one place on purpose and is pinned by tests — see CLAUDE.md invariant 6 and
 * docs/04-architecture.md 4.11 (N-S-13).
 *
 * **Screen names are on it too (2026-09-23).** In path mode the first path
 * segment is the organization and the next is the screen
 * (`/mycompany/register`), so an organization called `register` would make
 * `/register` mean two things. It did, on the first preview with sign-in: a
 * bookmark from P0 opened `/register`, which read as "the organization
 * `register`", whose link led to `/register/register` and a 404. The URL
 * structure is fixed from P1 on (4.11), so every screen name — including ones
 * P1 has not built yet — is reserved now: `register` `places` `lists`
 * `settings` `members` `tags` `search` `invite` `join`.
 * `apps/web/test/screens.test.ts` fails if a screen is added without its name
 * being here. (`p0` needs no entry: two characters is already too short.)
 *
 * **So are the names Clerk puts in the zone (2026-09-25).** Production moved to
 * `<slug>.mapshare.fyi` (F-B-11), so a slug is now literally a DNS label next
 * to Clerk's production records: `clerk` (Frontend API), `accounts` (Account
 * Portal) and `clkmail` (mail), all DNS-only CNAMEs (docs/setup.md 3). An
 * explicit record wins over the wildcard, so an organization called `clerk`
 * would get a subdomain that never reaches the Worker — or, if the record ever
 * went away, Clerk's name would start resolving to someone's organization.
 * (`clk._domainkey` / `clk2._domainkey` are two labels deep and cannot collide.)
 */
export const RESERVED_SLUGS: readonly string[] = [
  'about',
  'account',
  'accounts',
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'billing',
  'blog',
  'cdn',
  'clerk',
  'clkmail',
  'dev',
  'docs',
  'ftp',
  'help',
  'imap',
  'img',
  'invite',
  'join',
  'legal',
  'lists',
  'login',
  'mail',
  'mapshare',
  'members',
  'null',
  'places',
  'preview',
  'privacy',
  'register',
  'search',
  'settings',
  'smtp',
  'staging',
  'static',
  'status',
  'stg',
  'support',
  'tags',
  'terms',
  'test',
  'undefined',
  'www',
];

/**
 * Prefixes that mark an environment rather than a tenant.
 *
 * Blocked so that (a) staging can later move to a prefix scheme without a slug
 * collision, and (b) a misrouted request that lands on the production Worker
 * always 404s instead of resolving a real organization.
 */
export const RESERVED_SLUG_PREFIXES: readonly string[] = [
  'dev-',
  'pr-',
  'preview-',
  'staging-',
  'stg-',
  'test-',
];

export type SlugRejectionReason = 'invalid_format' | 'reserved_word' | 'reserved_prefix';

export type SlugCheckResult =
  { ok: true; slug: string } | { ok: false; reason: SlugRejectionReason };

/**
 * Normalizes and validates an organization slug.
 *
 * Slugs are case-insensitive; the normalized (lowercased, trimmed) form is what
 * gets stored and what `Host` is matched against.
 */
export function checkOrgSlug(input: string): SlugCheckResult {
  const slug = input.trim().toLowerCase();

  if (!orgSlugSchema.safeParse(slug).success) {
    return { ok: false, reason: 'invalid_format' };
  }
  if (RESERVED_SLUGS.includes(slug)) {
    return { ok: false, reason: 'reserved_word' };
  }
  if (RESERVED_SLUG_PREFIXES.some((prefix) => slug.startsWith(prefix))) {
    return { ok: false, reason: 'reserved_prefix' };
  }
  return { ok: true, slug };
}

export function isReservedOrgSlug(input: string): boolean {
  const result = checkOrgSlug(input);
  return !result.ok && result.reason !== 'invalid_format';
}
